import { useCallback, useEffect, useState } from "react";

export interface ClickMods {
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
}

/** What a left-click resolves to: open the details panel, or only mutate the selection. */
export type ClickIntent = "open" | "select";

/**
 * Multi-select state for a task list, modelled on MS To Do / file managers:
 *  - plain click  → single-select + open details ("open")
 *  - Cmd/Ctrl+click → toggle that row in/out of the set ("select")
 *  - Shift+click  → range from the anchor to the clicked row ("select")
 *  - Escape       → clear
 *
 * `orderedIds` is the flat, top-to-bottom order of currently visible rows — the axis
 * shift-range walks. Ids that leave that list (deleted / filtered out) are pruned.
 */
export function useTaskSelection(orderedIds: string[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [anchorId, setAnchorId] = useState<string | null>(null);

  // Drop ids that vanished from view (after a reload, delete, or filter change).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(orderedIds);
      const next = new Set<string>();
      let changed = false;
      for (const id of prev) {
        if (present.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [orderedIds]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") clear();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [clear]);

  const handleClick = useCallback(
    (id: string, mods: ClickMods): ClickIntent => {
      if (mods.shiftKey && anchorId) {
        const a = orderedIds.indexOf(anchorId);
        const b = orderedIds.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          setSelectedIds(new Set(orderedIds.slice(lo, hi + 1)));
          return "select";
        }
      }

      if (mods.metaKey || mods.ctrlKey) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
        setAnchorId(id);
        return "select";
      }

      setSelectedIds(new Set([id]));
      setAnchorId(id);
      return "open";
    },
    [anchorId, orderedIds],
  );

  /**
   * Resolve the effective selection for a right-click: keep the current set when the
   * clicked row is part of a multi-selection, otherwise reset to just that row.
   * Returns the ids the context menu should act on.
   */
  const selectForContext = useCallback(
    (id: string): string[] => {
      if (selectedIds.has(id) && selectedIds.size > 1) return [...selectedIds];
      setSelectedIds(new Set([id]));
      setAnchorId(id);
      return [id];
    },
    [selectedIds],
  );

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const dragGroupFor = useCallback(
    (id: string): string[] | undefined => {
      if (!selectedIds.has(id) || selectedIds.size < 2) return undefined;
      return orderedIds.filter((orderedId) => selectedIds.has(orderedId));
    },
    [selectedIds, orderedIds],
  );

  return { selectedIds, isSelected, handleClick, selectForContext, clear, dragGroupFor };
}
